import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { ContentWrapper } from "../loading/LoadingSkeleton";
import { Helmet } from "react-helmet-async";
import { API_URL } from "../../config";

// FilmTile component for individual fade-in animation
function FilmTile({ film, isMobile, keyPrefix, showReleaseDate = false }) {
	const [imageLoaded, setImageLoaded] = useState(false);
	const [imageSrc, setImageSrc] = useState('');

	useEffect(() => {
		// Start with thumbnail for faster loading, then upgrade to medium
		const thumbnail = film.attributes.still?.data?.attributes?.formats?.thumbnail?.url;
		const medium = film.attributes.still?.data?.attributes?.formats?.medium?.url;

		if (thumbnail) {
			setImageSrc(thumbnail);
			// Preload thumbnail and set loaded state
			const thumbnailImg = new Image();
			thumbnailImg.onload = () => {
				setImageLoaded(true);
				// Then preload medium image in background
				if (medium && medium !== thumbnail) {
					const mediumImg = new Image();
					mediumImg.onload = () => setImageSrc(medium);
					mediumImg.onerror = () => console.error('Failed to load medium image for', film.attributes.titel);
					mediumImg.src = medium;
				}
			};
			thumbnailImg.onerror = () => console.error('Failed to load thumbnail for', film.attributes.titel);
			thumbnailImg.src = thumbnail;
		} else if (medium) {
			setImageSrc(medium);
			// Preload medium image
			const mediumImg = new Image();
			mediumImg.onload = () => setImageLoaded(true);
			mediumImg.onerror = () => console.error('Failed to load medium image for', film.attributes.titel);
			mediumImg.src = medium;
		}
	}, [film]);

	if (isMobile) {
		return (
			<Link
				className={`films-container ${imageLoaded ? 'loaded' : 'loading'}`}
				key={keyPrefix + film.attributes.titel}
				to={"/films/" + film.attributes.productionid}
				style={{
					opacity: imageLoaded ? 1 : 0,
					transition: 'opacity 0.2s ease-in-out'
				}}
			>
				<img
					src={imageSrc}
					alt={film.attributes.titel}
					loading="lazy"
					onLoad={() => setImageLoaded(true)}
				/>
				<div className='films-details'>
					<h3 className='films-details-title'>
						{film.attributes.titel}
					</h3>
					<p>
						{film.attributes.regie}{" "}
						{"(" + (showReleaseDate ? film.attributes.release : film.attributes.jaar) + ")"}
					</p>
				</div>
			</Link>
		);
	}

	return (
		<Link
			className={`films-container ${imageLoaded ? 'loaded' : 'loading'}`}
			key={keyPrefix + film.attributes.titel}
			to={"/films/" + film.attributes.productionid}
			style={{
				backgroundImage: imageLoaded && imageSrc ? `url(${imageSrc})` : 'none',
				opacity: imageLoaded ? 1 : 0,
				transition: 'opacity 0.2s ease-in-out, background-image 0.2s ease-in-out'
			}}
		>
			<div className='films-details'>
				<h3 className='films-details-title'>
					{film.attributes.titel}
				</h3>
				<p>
					{film.attributes.regie}{" "}
					{showReleaseDate ? (
						<><br />{"(" + film.attributes.release + ")"}</>
					) : (
						"(" + film.attributes.jaar + ")"
					)}
				</p>
			</div>
		</Link>
	);
}

export default function Films() {
	const [films, setFilms] = useState([]);
	const [speciaal, setSpeciaal] = useState([]);
	const [verwachte, setVerwachte] = useState([]);
	const [loading, setLoading] = useState(true);
	const isMobile = useMediaQuery({ query: "(max-width: 991px)" });
	const location = useLocation();

	useEffect(() => {
		const loadAllData = async () => {
			try {
				// Check for cached data first
				const cacheKey = 'kriterion-films-data';
				const cacheTimeKey = 'kriterion-films-data-time';
				const cachedData = localStorage.getItem(cacheKey);
				const cacheTime = localStorage.getItem(cacheTimeKey);

				// Use cache if less than 5 minutes old
				if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < 300000) {
					const parsed = JSON.parse(cachedData);
					setFilms(parsed.films || []);
					setSpeciaal(parsed.speciaal || []);
					setVerwachte(parsed.verwachte || []);
					setLoading(false);
					return;
				}

				// Safari-compatible axios configuration
				const axiosConfig = {
					timeout: 10000, // 10 second timeout for Safari
					headers: {
						'Accept': 'application/json, text/plain, */*',
						'Content-Type': 'application/json'
					},
					withCredentials: false // Disable credentials for CORS
				};

				console.log('Starting API requests (Safari debugging)...');

				const [filmsRes, speciaalsRes, verwachteRes] = await Promise.all([
					axios.get(
						`${API_URL}/api/films?populate=*&pagination[page]=1&pagination[pageSize]=100&filters[$or][0][IsSpeciaal][$null]=true&filters[$or][1][IsSpeciaal][$eqi]=false&filters[release][$lte]=${formatDate(new Date())}&sort=release:desc`,
						axiosConfig
					),
					axios.get(
						`${API_URL}/api/films?populate=*&pagination[page]=1&pagination[pageSize]=100&filters[IsSpeciaal][$eqi]=true&sort=release:asc`,
						axiosConfig
					),
					axios.get(
						`${API_URL}/api/films?populate=*&pagination[page]=1&pagination[pageSize]=100&filters[$or][0][IsSpeciaal][$null]=true&filters[$or][1][IsSpeciaal][$eqi]=false&filters[release][$gt]=${formatDate(new Date())}&sort=release:asc`,
						axiosConfig
					)
				]);

				console.log('API requests completed successfully:', {
					films: filmsRes.data.data?.length,
					speciaal: speciaalsRes.data.data?.length,
					verwachte: verwachteRes.data.data?.length
				});

				const filmsData = filmsRes.data.data;
				const speciaalsData = speciaalsRes.data.data;
				const verwachteData = verwachteRes.data.data;

				// Cache the results
				const dataToCache = {
					films: filmsData,
					speciaal: speciaalsData,
					verwachte: verwachteData
				};
				localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
				localStorage.setItem(cacheTimeKey, Date.now().toString());

				setFilms(filmsData);
				setSpeciaal(speciaalsData);
				setVerwachte(verwachteData);
				setLoading(false);
			} catch (error) {
				console.error('Axios request failed (Safari debugging):', {
					message: error.message,
					status: error.response?.status,
					statusText: error.response?.statusText,
					url: error.config?.url
				});

				// Safari fallback: try with native fetch
				console.log('Attempting fetch fallback for Safari...');
				try {
					const baseUrl = `${API_URL}/api/films?populate=*&pagination[page]=1&pagination[pageSize]=100`;
					const fetchOptions = {
						method: 'GET',
						headers: {
							'Accept': 'application/json',
							'Content-Type': 'application/json'
						},
						mode: 'cors',
						credentials: 'omit',
						cache: 'no-cache'
					};

					const [filmsRes, speciaalsRes, verwachteRes] = await Promise.all([
						fetch(`${baseUrl}&filters[$or][0][IsSpeciaal][$null]=true&filters[$or][1][IsSpeciaal][$eqi]=false&filters[release][$lte]=${formatDate(new Date())}&sort=release:desc`, fetchOptions),
						fetch(`${baseUrl}&filters[IsSpeciaal][$eqi]=true&sort=release:asc`, fetchOptions),
						fetch(`${baseUrl}&filters[$or][0][IsSpeciaal][$null]=true&filters[$or][1][IsSpeciaal][$eqi]=false&filters[release][$gt]=${formatDate(new Date())}&sort=release:asc`, fetchOptions)
					]);

					if (!filmsRes.ok || !speciaalsRes.ok || !verwachteRes.ok) {
						throw new Error('Fetch requests failed');
					}

					const [filmsData, speciaalsData, verwachteData] = await Promise.all([
						filmsRes.json(),
						speciaalsRes.json(),
						verwachteRes.json()
					]);

					console.log('Fetch fallback successful:', {
						films: filmsData.data?.length,
						speciaal: speciaalsData.data?.length,
						verwachte: verwachteData.data?.length
					});

					// Cache the results
					const dataToCache = {
						films: filmsData.data,
						speciaal: speciaalsData.data,
						verwachte: verwachteData.data
					};
					localStorage.setItem('kriterion-films-data', JSON.stringify(dataToCache));
					localStorage.setItem('kriterion-films-data-time', Date.now().toString());

					setFilms(filmsData.data);
					setSpeciaal(speciaalsData.data);
					setVerwachte(verwachteData.data);
					setLoading(false);
				} catch (fetchError) {
					console.error('Fetch fallback also failed:', fetchError);
					setLoading(false);
				}
			}
		};

		loadAllData();
	}, []);

	return (
		<ContentWrapper isLoading={loading} loadingMessage="Films worden geladen...">
			<Helmet>
				<title>{location.pathname === '/' ? 'Filmtheater Kriterion Amsterdam' : 'Actuele Films - Filmtheater Kriterion Amsterdam'}</title>
				<meta name="description" content={location.pathname === '/' ? 'Non-hiërarchisch filmtheater en café gerund door 45 studenten en één kat.' : 'Bekijk alle films die nu draaien in Filmtheater Kriterion Amsterdam. Bestel direct tickets.'} />
			</Helmet>
			<div className='body-container'>
				{isMobile && <h1 className={`titel`}>Nu te zien</h1>} 
				{isMobile && <div className="top-border">
					{films.map((film) => (
						<FilmTile key={'mobilereg-' + film.attributes.titel} film={film} isMobile={isMobile} keyPrefix="mobilereg-" />
					))}
				</div>}	
				
				{isMobile && <h1 className="titel">Speciaal</h1>}
				{isMobile && <div className="top-border">
					{speciaal.map((film) => (
						<FilmTile key={'mobilespec-' + film.attributes.titel} film={film} isMobile={isMobile} keyPrefix="mobilespec-" />
					))}
				</div>}
				 
				{isMobile && <h1 className="titel">Verwacht</h1>}
				{isMobile && <div className="top-border">
					{verwachte.map((verwacht) => (
						<FilmTile key={'mobileverw-' + verwacht.attributes.titel} film={verwacht} isMobile={isMobile} keyPrefix="mobileverw-" showReleaseDate={true} />
					))}
				</div>}

				{!isMobile && (
					<div className="desktop-films-titel-wrapper">
						<h2 className='desktop-titel'>Nu te zien</h2>
						<div className='desktop-films-wrapper'>
							{films.map((film) => (
								<FilmTile key={'regulier-' + film.attributes.titel} film={film} isMobile={isMobile} keyPrefix="regulier-" />
							))}
						</div>

						<h2 className='desktop-titel'>Speciaal</h2>
						<div className='desktop-films-wrapper'>
							{speciaal.map((film) => (
								<FilmTile key={'spec-' + film.attributes.titel} film={film} isMobile={isMobile} keyPrefix="spec-" />
							))}
						</div>

						<h2 className='desktop-titel'>Verwacht</h2>
						<div className='desktop-films-wrapper'>
							{verwachte.map((verwacht) => (
								<FilmTile key={'verw-' + verwacht.attributes.titel} film={verwacht} isMobile={isMobile} keyPrefix="verw-" showReleaseDate={true} />
							))}
						</div>
					</div>
				)}
			</div>
		</ContentWrapper>
	);
}

function formatDate(date) {
    var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

    if (month.length < 2) 
        month = '0' + month;
    if (day.length < 2) 
        day = '0' + day;

    return [year, month, day].join('-');
}